import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './FlightInquiry.css';
import { COMPANY } from '../data/ItineraryData';
import { AIRPORTS, POPULAR_AIRPORTS } from '../data/Airports';
import { supabase } from '../lib/supabase';
import logo from '../Gallery/logo.png';



const INIT = {
    tripType: 'round', from: '', to: '', depart: '', returnDate: '',
    adults: 1, children: 0, infants: 0, cabinClass: 'economy',
    name: '', phone: '', email: '', notes: '',
};

const CABINS = [
    { val: 'economy', label: 'Economy', icon: 'fa-solid fa-person-seat' },
    { val: 'premium', label: 'Premium Eco', icon: 'fa-solid fa-person-seat' },
    { val: 'business', label: 'Business', icon: 'fa-solid fa-briefcase' },
    { val: 'first', label: 'First Class', icon: 'fa-solid fa-crown' },
];

const FlightInquiry = () => {
    const navigate = useNavigate();
    const [form, setForm] = useState(INIT);
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // Autocomplete states
    const [focusedField, setFocusedField] = useState(null);

    const set = (field, val) => setForm(f => ({ ...f, [field]: val }));
    const counter = (field, delta) => setForm(f => ({
        ...f, [field]: Math.max(field === 'adults' ? 1 : 0, (f[field] || 0) + delta),
    }));

    // Filter airports based on search query
    const getSuggestions = (query) => {
        if (!query) return [];
        const low = query.toLowerCase();
        return AIRPORTS.filter(a =>
            a.city.toLowerCase().includes(low) ||
            a.country.toLowerCase().includes(low) ||
            a.code.toLowerCase().includes(low)
        ).slice(0, 6); // Max 6 suggestions
    };

    const handleCommit = (field) => {
        setFocusedField(null);
        setForm(f => {
            const query = f[field];
            if (!query || query.includes('(')) return f;
            const sugs = AIRPORTS.filter(a => {
                const low = query.toLowerCase();
                return a.city.toLowerCase().includes(low) || a.country.toLowerCase().includes(low) || a.code.toLowerCase().includes(low);
            });
            if (sugs.length > 0) return { ...f, [field]: `${sugs[0].city} (${sugs[0].code})` };
            return f;
        });
    };

    const handleKeyDown = (e, field) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
            handleCommit(field);
            if (e.key === 'Enter') e.preventDefault();
        }
    };

    const validate = () => {
        const e = {};
        if (!form.from.trim()) e.from = 'Enter departure city / airport';
        if (!form.to.trim()) e.to = 'Enter destination city / airport';
        if (!form.depart) e.depart = 'Select departure date';
        if (form.tripType === 'round' && !form.returnDate) e.returnDate = 'Select return date';
        if (!form.name.trim()) e.name = 'Enter your full name';
        if (!form.phone.trim()) e.phone = 'Enter contact number';
        if (!form.email.trim()) e.email = 'Enter email address';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);
        await supabase.from('inquiries').insert([{
            type: 'flight',
            name: form.name,
            phone: form.phone,
            email: form.email,
            from_city: form.from,
            to_city: form.to,
            travel_date: form.depart || null,
            passengers: form.adults + form.children + form.infants,
            notes: `Trip: ${form.tripType} | Return: ${form.returnDate || 'N/A'} | Class: ${form.cabinClass} | Notes: ${form.notes}`,
            status: 'new',
        }]);
        setLoading(false);
        setSubmitted(true);
    };

    const totalPax = form.adults + form.children + form.infants;

    /* ── Success screen ── */
    if (submitted) {
        return (
            <div className="fi-page">
                <div className="fi-success">
                    <div className="fi-success-card">
                        <div className="fi-success-icon"><i className="fa-solid fa-circle-check"></i></div>
                        <h2>Inquiry Submitted!</h2>
                        <p>Thank you, <strong>{form.name}</strong>! Our team will contact you within <strong>2 hours</strong> with the best flight options.</p>
                        <div className="fi-success-details">
                            <div><i className="fa-solid fa-plane-departure"></i> {form.from} → {form.to}</div>
                            <div><i className="fa-solid fa-calendar"></i> {form.depart}{form.tripType === 'round' ? ` → ${form.returnDate}` : ''}</div>
                            <div><i className="fa-solid fa-users"></i> {totalPax} Passenger{totalPax > 1 ? 's' : ''} · {CABINS.find(c => c.val === form.cabinClass)?.label}</div>
                        </div>
                        <div className="fi-success-contact">
                            <span><i className="fa-solid fa-phone"></i> {COMPANY.phone}</span>
                            <span><i className="fa-solid fa-envelope"></i> {COMPANY.email}</span>
                        </div>
                        <button className="fi-btn-home" onClick={() => navigate('/')}>
                            <i className="fa-solid fa-house"></i> Back to Home
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fi-page">

            {/* ── HERO ── */}
            <div className="fi-hero">
                <div className="fi-hero-overlay" />
                <div className="fi-hero-content">
                    <h1 className="fi-hero-title">
                        <i className="fa-solid fa-plane"></i> Flight Inquiry
                    </h1>
                    <p className="fi-hero-sub">Tell us where you want to fly — we'll find you the best fares</p>
                </div>
            </div>

            {/* ── FORM AREA ── */}
            <div className="fi-body">
                <form className="fi-form" onSubmit={handleSubmit} noValidate>

                    {/* Trip Type */}
                    <div className="fi-card">
                        <h3 className="fi-card-title"><i className="fa-solid fa-route"></i> Trip Type</h3>
                        <div className="fi-trip-tabs">
                            {[
                                { val: 'oneway', label: 'One Way', icon: 'fa-solid fa-arrow-right' },
                                { val: 'round', label: 'Round Trip', icon: 'fa-solid fa-arrows-rotate' },
                                { val: 'multi', label: 'Multi-City', icon: 'fa-solid fa-code-branch' },
                            ].map(t => (
                                <button type="button" key={t.val}
                                    className={`fi-trip-tab ${form.tripType === t.val ? 'active' : ''}`}
                                    onClick={() => set('tripType', t.val)}>
                                    <i className={t.icon}></i> {t.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Route */}
                    <div className="fi-card">
                        <h3 className="fi-card-title"><i className="fa-solid fa-map-location-dot"></i> Route</h3>
                        <div className="fi-route-row">
                            <div className="fi-field-wrap">
                                <label>From</label>
                                <div className={`fi-input-group ${errors.from ? 'error' : ''}`}>
                                    <i className="fa-solid fa-plane-departure fi-input-icon"></i>
                                    <input type="text" placeholder="City or Airport" value={form.from}
                                        onChange={e => set('from', e.target.value)}
                                        onFocus={() => setFocusedField('from')}
                                        onKeyDown={e => handleKeyDown(e, 'from')}
                                        onBlur={() => setTimeout(() => handleCommit('from'), 200)}
                                    />
                                    {focusedField === 'from' && form.from && getSuggestions(form.from).length > 0 && (
                                        <div className="fi-autocomplete-dropdown">
                                            {getSuggestions(form.from).map(a => (
                                                <div key={a.code} className="fi-autocomplete-item" onClick={() => {
                                                    set('from', `${a.city} (${a.code})`);
                                                    setFocusedField(null);
                                                }}>
                                                    <div className="fi-ac-main"><span>{a.city}</span> <span className="fi-ac-code">{a.code}</span></div>
                                                    <div className="fi-ac-sub">{a.country}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {errors.from && <span className="fi-err">{errors.from}</span>}
                                <div className="fi-quick-airports">
                                    {POPULAR_AIRPORTS.slice(0, 4).map(a => (
                                        <button type="button" key={a.code} className="fi-airport-chip"
                                            onClick={() => set('from', `${a.city} (${a.code})`)}>{a.city}</button>
                                    ))}
                                </div>
                            </div>

                            <button type="button" className="fi-swap-btn"
                                onClick={() => setForm(f => ({ ...f, from: f.to, to: f.from }))}>
                                <i className="fa-solid fa-right-left"></i>
                            </button>

                            <div className="fi-field-wrap">
                                <label>To</label>
                                <div className={`fi-input-group ${errors.to ? 'error' : ''}`}>
                                    <i className="fa-solid fa-plane-arrival fi-input-icon"></i>
                                    <input type="text" placeholder="City or Airport" value={form.to}
                                        onChange={e => set('to', e.target.value)}
                                        onFocus={() => setFocusedField('to')}
                                        onKeyDown={e => handleKeyDown(e, 'to')}
                                        onBlur={() => setTimeout(() => handleCommit('to'), 200)}
                                    />
                                    {focusedField === 'to' && form.to && getSuggestions(form.to).length > 0 && (
                                        <div className="fi-autocomplete-dropdown">
                                            {getSuggestions(form.to).map(a => (
                                                <div key={a.code} className="fi-autocomplete-item" onClick={() => {
                                                    set('to', `${a.city} (${a.code})`);
                                                    setFocusedField(null);
                                                }}>
                                                    <div className="fi-ac-main"><span>{a.city}</span> <span className="fi-ac-code">{a.code}</span></div>
                                                    <div className="fi-ac-sub">{a.country}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {errors.to && <span className="fi-err">{errors.to}</span>}
                                <div className="fi-quick-airports">
                                    {POPULAR_AIRPORTS.slice(4, 8).map(a => (
                                        <button type="button" key={a.code} className="fi-airport-chip"
                                            onClick={() => set('to', `${a.city} (${a.code})`)}>{a.city}</button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Dates */}
                    <div className="fi-card">
                        <h3 className="fi-card-title"><i className="fa-solid fa-calendar-days"></i> Travel Dates</h3>
                        <div className="fi-dates-row">
                            <div className="fi-field-wrap">
                                <label>Departure Date</label>
                                <div className={`fi-input-group ${errors.depart ? 'error' : ''}`}>
                                    <i className="fa-solid fa-calendar fi-input-icon"></i>
                                    <input type="date" min={new Date().toISOString().split('T')[0]}
                                        value={form.depart} onChange={e => set('depart', e.target.value)} />
                                </div>
                                {errors.depart && <span className="fi-err">{errors.depart}</span>}
                            </div>
                            {form.tripType === 'round' && (
                                <div className="fi-field-wrap">
                                    <label>Return Date</label>
                                    <div className={`fi-input-group ${errors.returnDate ? 'error' : ''}`}>
                                        <i className="fa-solid fa-calendar-check fi-input-icon"></i>
                                        <input type="date" min={form.depart || new Date().toISOString().split('T')[0]}
                                            value={form.returnDate} onChange={e => set('returnDate', e.target.value)} />
                                    </div>
                                    {errors.returnDate && <span className="fi-err">{errors.returnDate}</span>}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Passengers & Class */}
                    <div className="fi-card">
                        <h3 className="fi-card-title"><i className="fa-solid fa-users"></i> Passengers & Class</h3>
                        <div className="fi-pax-grid">
                            {[
                                { field: 'adults', label: 'Adults', sub: '12+ years' },
                                { field: 'children', label: 'Children', sub: '2–11 years' },
                                { field: 'infants', label: 'Infants', sub: 'Under 2' },
                            ].map(p => (
                                <div key={p.field} className="fi-pax-card">
                                    <div className="fi-pax-label">{p.label}</div>
                                    <div className="fi-pax-sub">{p.sub}</div>
                                    <div className="fi-pax-counter">
                                        <button type="button" className="fi-cnt-btn" onClick={() => counter(p.field, -1)}>−</button>
                                        <span className="fi-cnt-val">{form[p.field]}</span>
                                        <button type="button" className="fi-cnt-btn" onClick={() => counter(p.field, +1)}>+</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="fi-cabin-grid">
                            {CABINS.map(c => (
                                <button type="button" key={c.val}
                                    className={`fi-cabin-btn ${form.cabinClass === c.val ? 'active' : ''}`}
                                    onClick={() => set('cabinClass', c.val)}>
                                    <i className={c.icon}></i><span>{c.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Contact */}
                    <div className="fi-card">
                        <h3 className="fi-card-title"><i className="fa-solid fa-address-card"></i> Your Contact Details</h3>
                        <div className="fi-contact-grid">
                            <div className="fi-field-wrap">
                                <label>Full Name *</label>
                                <div className={`fi-input-group ${errors.name ? 'error' : ''}`}>
                                    <i className="fa-solid fa-user fi-input-icon"></i>
                                    <input type="text" placeholder="Your full name" value={form.name}
                                        onChange={e => set('name', e.target.value)} />
                                </div>
                                {errors.name && <span className="fi-err">{errors.name}</span>}
                            </div>
                            <div className="fi-field-wrap">
                                <label>Phone *</label>
                                <div className={`fi-input-group ${errors.phone ? 'error' : ''}`}>
                                    <i className="fa-solid fa-phone fi-input-icon"></i>
                                    <input type="tel" placeholder="+91 XXXXX XXXXX" value={form.phone}
                                        onChange={e => set('phone', e.target.value)} />
                                </div>
                                {errors.phone && <span className="fi-err">{errors.phone}</span>}
                            </div>
                            <div className="fi-field-wrap fi-field-full">
                                <label>Email *</label>
                                <div className={`fi-input-group ${errors.email ? 'error' : ''}`}>
                                    <i className="fa-solid fa-envelope fi-input-icon"></i>
                                    <input type="email" placeholder="you@example.com" value={form.email}
                                        onChange={e => set('email', e.target.value)} />
                                </div>
                                {errors.email && <span className="fi-err">{errors.email}</span>}
                            </div>
                            <div className="fi-field-wrap fi-field-full">
                                <label>Special Requests</label>
                                <div className="fi-input-group fi-textarea-wrap">
                                    <i className="fa-solid fa-note-sticky fi-input-icon fi-textarea-icon"></i>
                                    <textarea rows={3} placeholder="Meal preferences, preferred airlines, flexible dates..."
                                        value={form.notes} onChange={e => set('notes', e.target.value)} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Summary */}
                    <div className="fi-summary">
                        <div className="fi-summary-items">
                            {form.from && form.to && (
                                <span className="fi-sum-item"><i className="fa-solid fa-plane"></i> {form.from} → {form.to}</span>
                            )}
                            {form.depart && (
                                <span className="fi-sum-item">
                                    <i className="fa-solid fa-calendar"></i> {form.depart}
                                    {form.tripType === 'round' && form.returnDate ? ` – ${form.returnDate}` : ''}
                                </span>
                            )}
                            <span className="fi-sum-item"><i className="fa-solid fa-users"></i> {totalPax} pax</span>
                            <span className="fi-sum-item"><i className="fa-solid fa-chair"></i> {CABINS.find(c => c.val === form.cabinClass)?.label}</span>
                        </div>
                        <button type="submit" className={`fi-submit-btn ${loading ? 'loading' : ''}`} disabled={loading}>
                            {loading
                                ? <><span className="fi-spinner" /> Submitting…</>
                                : <><i className="fa-solid fa-paper-plane"></i> Submit Flight Inquiry</>}
                        </button>
                    </div>
                </form>

                {/* Sidebar */}
                <aside className="fi-sidebar">
                    <div className="fi-sidebar-card">
                        <img src={logo} alt="Silver Sky Holidays" className="fi-sb-logo" />
                        <div className="fi-sb-name">{COMPANY.name}</div>
                        <div className="fi-sb-tag">{COMPANY.tagline}</div>
                        <div className="fi-sb-tagline-line"></div>
                        <div className="fi-sb-cert">{COMPANY.cert}</div>
                    </div>
                    <div className="fi-sidebar-card fi-why-card">
                        <h4><i className="fa-solid fa-star"></i> Why Book With Us?</h4>
                        <ul>
                            {['Best negotiated airline fares', 'Group & corporate discounts', 'Free seat selection assistance', '24/7 customer support', 'Hassle-free visa guidance', 'Flexible cancellation help']
                                .map((p, i) => <li key={i}><i className="fa-solid fa-check"></i> {p}</li>)}
                        </ul>
                    </div>
                    <div className="fi-sidebar-card fi-contact-card">
                        <h4><i className="fa-solid fa-headset"></i> Talk to Us</h4>
                        <div className="fi-contact-item">
                            <i className="fa-solid fa-phone"></i>
                            <div>
                                <span className="fi-contact-label">Call / WhatsApp</span>
                                <a href={`tel:${COMPANY.phone}`}>{COMPANY.phone}</a>
                                <a href={`tel:${COMPANY.altPhone}`}>{COMPANY.altPhone}</a>
                            </div>
                        </div>
                        <div className="fi-contact-item">
                            <i className="fa-solid fa-envelope"></i>
                            <div>
                                <span className="fi-contact-label">Email</span>
                                <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
                            </div>
                        </div>
                        <div className="fi-contact-item">
                            <i className="fa-solid fa-location-dot"></i>
                            <div>
                                <span className="fi-contact-label">Office</span>
                                <span>{COMPANY.address}</span>
                            </div>
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default FlightInquiry;
